import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
  { label: "Competencies", hash: "#about" },
  { label: "Physical Metals", hash: "#physical" },
  { label: "Risk & Derivatives", hash: "#derivatives" },
  { label: "Logistics", hash: "#logistics" },
];

export default function Nav({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    onNavigate(hash);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0D12]/85 border-b border-[#E06D28]/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => go(e, "#top")}
          className="flex items-center gap-3 group"
        >
          <span className="transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
            <Logo size={30} />
          </span>
          <span className="font-display font-bold tracking-wide text-[#F4F6F8]">
            ACPZ <span className="text-[#E06D28]">CONSULTANCY</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <a
              key={l.hash}
              href={l.hash}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={(e) => go(e, l.hash)}
              className="text-sm font-medium text-[#94A3B8] hover:text-[#F07D38] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href="tel:+447961967908"
            data-testid="nav-phone-button"
            className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full border border-[#E06D28]/50 text-[#F07D38] hover:bg-[#E06D28] hover:text-white transition-all duration-300"
          >
            <Phone size={14} />
            +44 7961 967908
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#F4F6F8] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-[#0A0D12]/95 border-b border-[#E06D28]/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.hash}
                  href={l.hash}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={(e) => go(e, l.hash)}
                  className="text-base font-medium text-[#94A3B8] hover:text-[#F07D38] transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+447961967908"
                data-testid="nav-mobile-phone"
                className="flex items-center gap-2 text-sm font-semibold text-[#F07D38]"
              >
                <Phone size={14} /> +44 7961 967908
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
